/* Core & libraries */
import React, { useState, useEffect } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import ContainerFluid from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import UCButton from "../../buttons/index.js";
import moment from "helpers/moment";

/* Helpers, constants, custom methods, internal libraries */
import { useLocaleContext } from "../../../contexts/localeContext";

/* Components */
import TextLoadingSpinner from "../../../components/spinners/textLoadingSpinner";

/* Styles */
import styles from "./styles.js";

const messages = defineMessages({
  tierAwardedOn: {
    id: "loyaltyChooseTier.tierAwardedOn",
  },
});

const Content = ({
  setShowModal,
  updateTier,
  activeUser,
  pointDifference,
  initialSelectedTier,
  tiersList,
  userPreviousTier,
  allowConciergeKey,
}) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const intl = useIntl();
  const globalLocale = useLocaleContext();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tiersList && userPreviousTier) {
      setLoading(false);
    }
  }, [tiersList, userPreviousTier]);

  const highestUserTierLevel = userPreviousTier?.redemptions?.reduce(
    (max, redemption) => Math.max(max, redemption.tierLevel),
    0
  );

  const highestUserRedemption = userPreviousTier?.redemptions?.find(
    (redemption) => redemption.tierLevel === highestUserTierLevel
  );

  const findFirstValidIndex = () => {
    if (!tiersList) return null;
    const firstAvailableIndex = tiersList.findIndex(
      (tier) =>
        !(
          tier.level <= highestUserTierLevel ||
          !tier.points ||
          tier.points > pointDifference ||
          (tier.tier === "C" && !allowConciergeKey)
        )
    );
    return firstAvailableIndex !== -1 ? firstAvailableIndex : null;
  };

  const [isChecked, setIsChecked] = useState(
    initialSelectedTier !== undefined && initialSelectedTier !== null
      ? initialSelectedTier
      : findFirstValidIndex()
  );

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <div className={classes.modalContent}>
      {loading ? (
        <TextLoadingSpinner />
      ) : (
        <div className={classes.listContainer}>
          {tiersList.map((item, id) => {
            const isDisabled =
              item.level <= highestUserTierLevel ||
              !item.points ||
              item.points > pointDifference ||
              (item.tier === "C" && !allowConciergeKey);

            let description = item.description;
            let message;

            if (item.level === highestUserTierLevel) {
              let redemptionDate = moment(highestUserRedemption.redemptionDate)
                .locale(globalLocale?.toLowerCase())
                .format("MMM D ‘YY");
              redemptionDate = capitalizeFirstLetter(redemptionDate);
              description = `${item.description}`;
              message = `${intl.formatMessage(
                messages.tierAwardedOn
              )} ${redemptionDate}`;
            } else if (isDisabled && item.level < highestUserTierLevel) {
              message = (
                <FormattedMessage id="loyaltyChooseTier.notAvailable" />
              );
            } else if (
              !item.points ||
              (item.tier === "C" && !allowConciergeKey)
            ) {
              message = (
                <FormattedMessage id="loyaltyChooseTier.notAvailable" />
              );
            } else {
              message = (
                <FormattedMessage
                  id="loyaltyChooseTier.points"
                  values={{ points: item?.points?.toLocaleString() }}
                />
              );
            }
            return (
              <label
                key={id}
                className={
                  isChecked === id ||
                  (isChecked === null && item.level === highestUserTierLevel)
                    ? classes.radioContainerActive
                    : isDisabled
                    ? classes.radioContainerDisabled
                    : classes.radioContainer
                }
              >
                <input
                  onChange={() => setIsChecked(id)}
                  type="radio"
                  name="radioOption"
                  id={id}
                  disabled={isDisabled}
                />
                <div
                  className={
                    isChecked === id ||
                    (isChecked === null && item.level === highestUserTierLevel)
                      ? classes.radioTextActive
                      : classes.radioText
                  }
                >
                  <h6>{description}</h6>
                  {message && <p>{message}</p>}
                </div>
                <div
                  className={`${
                    isChecked === id ||
                    (isChecked === null && item.level === highestUserTierLevel)
                      ? classes.checkmarkChecked
                      : ""
                  } ${classes.checkmark}`}
                ></div>
              </label>
            );
          })}
        </div>
      )}
      <div className={classes.btnContainer}>
        <ContainerFluid>
          <Row className={classes.btnsRow}>
            <Col className={classes.colCancel}>
              <UCButton
                onClick={() => setShowModal(false)}
                type="default"
                className={classes.cancelBtn}
                text={<FormattedMessage id="loyaltyChooseTier.cancel" />}
              />
            </Col>
            <Col className={classes.colChoose}>
              <UCButton
                className={classes.chooseBtn}
                onClick={() =>
                  updateTier(
                    activeUser.userEntityId,
                    tiersList[isChecked].description,
                    tiersList[isChecked].points,
                    tiersList[isChecked].level
                  )
                }
                type="primary"
                text={<FormattedMessage id="loyaltyChooseTier.continue" />}
                dataTestId="chooseTierModalBtn"
                disabled={isChecked === null || loading}
              />
            </Col>
          </Row>
        </ContainerFluid>
      </div>
    </div>
  );
};

export default Content;
